import { db } from "../services/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { LoggerService } from "../services/LoggerService";

export const usersService = {
    // Helper function to log admin actions
    async logAdminAction(adminId, action, targetUserId, details = {}) {
        try {
            const { adminAuditService } = await import("../services/adminAuditService");
            await adminAuditService.logUserManagementAction(adminId, action, targetUserId, {
                ...details,
                adminEmail: details.adminEmail || "unknown",
                ipAddress: "frontend",
                userAgent: navigator.userAgent,
                sessionId: "frontend_session"
            });
            // Logging administrativo local
            LoggerService.log("admin", "admin-actions", {
                adminId,
                action,
                targetUserId,
                details,
                service: "usersService"
            });
        } catch (error) {
            // Admin audit logging failed
            LoggerService.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "usersService",
                context: "logAdminAction"
            });
        }
    },

    // Acceso a perfil de usuario (acceso a PII)
    async getUserProfile(uid, requesterId = null) {
        try {
            const userDoc = await getDoc(doc(db, "users", uid));
            if (userDoc.exists()) {
                // Logging de acceso a PII
                LoggerService.log("pii", "access", {
                    userId: requesterId || uid,
                    action: "ACCESS",
                    entityType: "User",
                    entityId: uid,
                    pii: { email: userDoc.data().email ? "***" + userDoc.data().email.slice(-8) : undefined },
                    service: "usersService"
                });
                return userDoc.data();
            }
            return null;
        } catch (error) {
            LoggerService.log("system", "error", {
                error: error.message,
                stack: error.stack,
                service: "usersService",
                context: "getUserProfile"
            });
            return null;
        }
    },
  
    // Edición de perfil de usuario (incluye cambios de PII)
    async updateUserProfile(uid, updates, adminId = null) {
        const userRef = doc(db, "users", uid);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            LoggerService.log("system", "error", {
                error: "User not found",
                service: "usersService",
                context: "updateUserProfile",
                entityId: uid
            });
            throw new Error("User not found");
        }

        const oldData = userDoc.data();
        await updateDoc(userRef, updates);

        // Logging de edición general
        LoggerService.log("audit", "actions", {
            userId: adminId || uid,
            action: "UPDATE",
            entityType: "User",
            entityId: uid,
            details: { oldValue: oldData, newValue: { ...oldData, ...updates } },
            service: "usersService"
        });

        // Logging de cambios de PII
        if (updates.email || updates.displayName || updates.phoneNumber) {
            LoggerService.log("pii", "changes", {
                userId: adminId || uid,
                action: "UPDATE_PII",
                entityType: "User",
                entityId: uid,
                pii: {
                    email: updates.email ? "***" + updates.email.slice(-8) : undefined,
                    displayName: updates.displayName,
                    phoneNumber: updates.phoneNumber ? "***" + updates.phoneNumber.slice(-4) : undefined
                },
                service: "usersService"
            });
        }

        // Log admin action si aplica
        if (adminId) {
            await this.logAdminAction(adminId, "user_profile_updated", uid, {
                oldValue: oldData,
                newValue: { ...oldData, ...updates },
                reason: updates.reason || "Admin update"
            });
        }

        return true;
    },

    // Baja de usuario
    async deleteUser(uid, adminId = null) {
        // ... código de borrado ...
        // Logging de baja
        LoggerService.log("audit", "actions", {
            userId: adminId || uid,
            action: "DELETE",
            entityType: "User",
            entityId: uid,
            service: "usersService"
        });
        // Logging de PII
        LoggerService.log("pii", "changes", {
            userId: adminId || uid,
            action: "DELETE_PII",
            entityType: "User",
            entityId: uid,
            service: "usersService"
        });
        return true;
    },

    // Cambio de contraseña/email (PII)
    async changeUserPassword(uid, newPassword, adminId = null) {
        // ... código de cambio de contraseña ...
        LoggerService.log("pii", "changes", {
            userId: adminId || uid,
            action: "CHANGE_PASSWORD",
            entityType: "User",
            entityId: uid,
            pii: { password: "***" },
            service: "usersService"
        });
        return true;
    },

    // Login/logout
    async logUserLogin(uid) {
        LoggerService.log("audit", "actions", {
            userId: uid,
            action: "LOGIN",
            entityType: "User",
            entityId: uid,
            service: "usersService"
        });
    },
    async logUserLogout(uid) {
        LoggerService.log("audit", "actions", {
            userId: uid,
            action: "LOGOUT",
            entityType: "User",
            entityId: uid,
            service: "usersService"
        });
    },

    // Admin function to update user role
    async updateUserRole(adminId, targetUserId, newRole, reason = "") {
        const userRef = doc(db, "users", targetUserId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            LoggerService.log("system", "error", {
                error: "User not found",
                service: "usersService",
                context: "updateUserRole",
                entityId: targetUserId
            });
            throw new Error("User not found");
        }

        const oldData = userDoc.data();
        const oldRole = oldData.role;

        await updateDoc(userRef, {
            role: newRole,
            updatedAt: new Date().toISOString()
        });

        // Logging de cambio de rol
        LoggerService.log("admin", "admin-actions", {
            adminId,
            action: "ROLE_CHANGE",
            targetUserId,
            oldRole,
            newRole,
            reason,
            service: "usersService"
        });

        await this.logAdminAction(adminId, "user_role_updated", targetUserId, {
            oldValue: { role: oldRole },
            newValue: { role: newRole },
            reason: reason || "Admin role update"
        });

        return true;
    },

    // Admin function to disable/enable user
    async toggleUserStatus(adminId, targetUserId, newStatus, reason = "") {
        const userRef = doc(db, "users", targetUserId);
        const userDoc = await getDoc(userRef);
        
        if (!userDoc.exists()) {
            LoggerService.log("system", "error", {
                error: "User not found",
                service: "usersService",
                context: "toggleUserStatus",
                entityId: targetUserId
            });
            throw new Error("User not found");
        }

        const oldData = userDoc.data();
        const oldStatus = oldData.status;

        await updateDoc(userRef, {
            status: newStatus,
            updatedAt: new Date().toISOString()
        });

        // Logging de cambio de estado
        LoggerService.log("admin", "admin-actions", {
            adminId,
            action: "STATUS_CHANGE",
            targetUserId,
            oldStatus,
            newStatus,
            reason,
            service: "usersService"
        });

        await this.logAdminAction(adminId, "user_status_updated", targetUserId, {
            oldValue: { status: oldStatus },
            newValue: { status: newStatus },
            reason: reason || "Admin status update"
        });

        return true;
    }
}; 